/* @flow */

import * as React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import sortBy from 'lodash/sortBy'

import WebshopActionButton from '../WebshopActionButton'
import WebshopLinkButton from '../WebshopLinkButton'
import CloudinaryResource from '../../../../infrastructure/components/CloudinaryResource'
import FormatCurrency from '../../../../infrastructure/components/FormatCurrency'
import { useWebshopNavigationLinks, useWebshopPages } from '../../hooks'
import { SessionContext, Tooltip } from '../../../shared'
import { WebshopContext } from '../../shared'

import WebshopNavigation from '../Navigation/WebshopNavigation'
import WebshopCustomerSelectorModal from '../WebshopCustomerSelectorModal'

const WebshopHeaderClassic = ({ brand, outsideProps }) => {
  const { entity } = React.useContext(SessionContext)
  const {
    availableBrand,
    currency,
    favorites,
    query,
    shopUrl,
    refreshWebshopTotals,
    setUseWebshopSessionId,
    totals,
    updateWebshopSession,
    webshopSession,
    webshopSettings,
  } = React.useContext(WebshopContext)
  const {
    onSubmitSearch,
    setShowManualEditCustomerModal,
    setShowRetailerSelector,
    setShowSessionListModal,
  } = outsideProps

  const { customer, drop } = webshopSession

  const navigationFetchArgs = React.useMemo(() => {
    return [brand.id, webshopSession.id]
  }, [brand, webshopSession])

  const [
    navigationLinks,
    isFetchingNavigationLinks,
    { isInitialized: isNavigationLinksInitialized },
  ] = useWebshopNavigationLinks(navigationFetchArgs)

  const sortedNavigationLinks = React.useMemo(() => {
    return sortBy(navigationLinks, 'sort_order')
  }, [navigationLinks])

  const customerCanMakeDropShipping = React.useMemo(() => {
    return customer ? customer.dropshipping : false
  }, [customer])

  const onConvertToDropshippingOrder = React.useCallback(() => {
    return updateWebshopSession({
      dropshipping: true,
    })
  }, [updateWebshopSession])

  const onConvertToNormalOrder = React.useCallback(() => {
    return updateWebshopSession({
      dropshipping: false,
    })
  }, [updateWebshopSession])

  const availableConnections = availableBrand
    ? availableBrand.retailer_connections.filter(
        connection => connection.b2b_access
      )
    : []

  if (!isNavigationLinksInitialized) {
    return null
  }

  return (
    <>
      <HeaderContainer fullWidth={webshopSettings.full_width}>
        <WebhopSessionBox>
          <HeaderBoxContent>
            <HeaderBoxTitle>
              <HeaderBoxIcon>
                <span className="lnr lnr-store" />
              </HeaderBoxIcon>
              <span>Current Order</span>
            </HeaderBoxTitle>
            <HeaderBoxBody>
              {entity.entity_type === 'brand' && (
                <CustomerNameContainer>
                  <CustomerName>
                    Customer: {webshopSession.customer.name}
                  </CustomerName>

                  <EditCustomerButton
                    onClick={() => setShowManualEditCustomerModal(true)}
                  >
                    <span className="glyphicon glyphicon-pencil" />
                  </EditCustomerButton>
                </CustomerNameContainer>
              )}
              {entity.entity_type === 'shop' &&
                availableConnections.length > 1 && (
                  <div>
                    <span>Shopping as: {webshopSession.customer.name}</span>

                    <EditCustomerButton
                      onClick={() => setShowRetailerSelector(true)}
                    >
                      <span className="glyphicon glyphicon-pencil" />
                    </EditCustomerButton>
                  </div>
                )}

              {customerCanMakeDropShipping && (
                <div>
                  {!webshopSession.dropshipping && (
                    <WebshopActionButton
                      onClick={onConvertToDropshippingOrder}
                      size="xsmall"
                    >
                      Convert to dropshipping order
                    </WebshopActionButton>
                  )}

                  {webshopSession.dropshipping && (
                    <>
                      <span className="label label-xxs label-warning">
                        Dropshipping
                      </span>
                      <Tooltip
                        tip="Convert to normal order"
                        id="convert-to-normal-order"
                      >
                        <ConvertToNormalOrderButton
                          color="transparent"
                          onClick={onConvertToNormalOrder}
                          size="xsmall"
                        >
                          <span className="glyphicon glyphicon-share-alt" />
                        </ConvertToNormalOrderButton>
                      </Tooltip>
                    </>
                  )}
                </div>
              )}

              {drop && <div>Drop: {drop.name}</div>}
            </HeaderBoxBody>
            <HeaderBoxAction>
              <WebshopActionButton
                color="white"
                onClick={() => setShowSessionListModal(true)}
                size="small"
              >
                Manage open orders
              </WebshopActionButton>
            </HeaderBoxAction>
          </HeaderBoxContent>
        </WebhopSessionBox>

        <HeaderLogo>
          <Link to={shopUrl}>
            <CloudinaryResource
              brandId={brand.id}
              id={brand.logo}
              presets="logo_webshop"
              fallback="logo_black"
            />
          </Link>
        </HeaderLogo>

        <HeaderBoxes>
          <FavoritesBox>
            <HeaderBoxContent>
              <HeaderBoxTitle right>
                <HeaderBoxIcon>
                  <span className="lnr lnr-heart" />
                </HeaderBoxIcon>
                <span>Favorites</span>
              </HeaderBoxTitle>

              <HeaderBoxBody>
                <div>{favorites.total} products</div>
                <div>selected</div>
              </HeaderBoxBody>
              <HeaderBoxAction>
                <WebshopLinkButton
                  to={`${shopUrl}/favorites`}
                  className="webshop-go-to-favorites-btn"
                  color="white"
                  size="small"
                >
                  Go to Favorites
                </WebshopLinkButton>
              </HeaderBoxAction>
            </HeaderBoxContent>
          </FavoritesBox>
          <CartBox>
            <HeaderBoxContent>
              <HeaderBoxTitle right>
                <HeaderBoxIcon>
                  <span className="lnr lnr-cart" />
                </HeaderBoxIcon>
                <span>Cart</span>
              </HeaderBoxTitle>

              <HeaderBoxBody>
                <div>{totals.quantity} pieces</div>
                <div>
                  <FormatCurrency currency={currency}>
                    {totals.price}
                  </FormatCurrency>
                </div>
              </HeaderBoxBody>
              <HeaderBoxAction>
                <WebshopLinkButton
                  to={`${shopUrl}/cart`}
                  className="webshop-go-to-cart-btn"
                  color="white"
                  size="small"
                >
                  Go to Cart
                </WebshopLinkButton>
              </HeaderBoxAction>
            </HeaderBoxContent>
          </CartBox>
        </HeaderBoxes>
      </HeaderContainer>

      <WebshopNavigation
        handleSearchSubmit={onSubmitSearch}
        navigationLinks={sortedNavigationLinks}
        searchQuery={query.q || ''}
        shopUrl={shopUrl}
        brand={brand}
      />
    </>
  )
}

// Need class for custom CSS hook
const HeaderContainer = styled.div.attrs({ className: 'webshop-header' })`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-top: 30px;
  ${({ fullWidth }) =>
    fullWidth === true &&
    `
    padding-left: 15px;
    padding-right: 15px;
  `}
  position: relative;
`

const HeaderBoxes = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
  max-width: 230px;

  > * {
    flex: 1 0 0;
    text-align: right;
  }
`

const HeaderLogo = styled.div`
  flex: 1;
  left: 50%;
  margin-left: -125px;
  max-width: 250px;
  position: absolute;

  img {
    max-width: 100%;
  }
`

const HeaderBox = styled.div`
  display: flex;
  height: 110px;
  overflow-x: hidden;
`

const HeaderBoxBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    font-size: 85%;
  }
`

const HeaderBoxIcon = styled.div`
  display: inline-block;
  font-size: 20px;
  margin-right: 10px;
`

const HeaderBoxContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
`

const HeaderBoxTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ right }) => (right ? 'flex-end' : 'flex-start')};
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 1px;
  font-size: 11px;
`

const HeaderBoxAction = styled.div`
  margin-top: 10px;
`

const WebhopSessionBox = styled(HeaderBox)``

const CartBox = styled(HeaderBox)`
  ${HeaderBoxBody} {
    white-space: nowrap;
  }
`

const FavoritesBox = styled(HeaderBox)``

const CustomerNameContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`

const CustomerName = styled.div`
  max-width: 200px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @media (min-width: 992px) {
    max-width: 310px;
  }

  @media (min-width: 1200px) {
    max-width: 410px;
  }
`

const EditCustomerButton = styled.button.attrs({
  size: 'xsmall',
  type: 'button',
})`
  background: transparent;
  border: none;
  font-size: 10px;
  outline: 0;
`

const ConvertToNormalOrderButton = styled(WebshopActionButton)``

export default WebshopHeaderClassic
